import { useState } from "react";
import { MapPin, Send, MessageCircle, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import api from "@/services/api";
import { pixelLead } from "@/lib/pixel";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Enviar mensaje de contacto a través de la API
      const response = await api.post('/contact', formData);

      if (response.data.success) {
        pixelLead();
        toast({
          title: "Mensaje enviado",
          description: "Gracias por contactarnos. Te responderemos pronto.",
        });
        // Limpiar formulario
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        throw new Error(response.data.message || "Error al enviar el mensaje");
      }
    } catch (error: any) {
      console.error("Error al enviar mensaje de contacto:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "No se pudo enviar el mensaje. Por favor, contáctanos por WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-purple/5">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-brown mb-6">
            Contáctenos
          </h1>
          <p className="text-xl text-brown/80 max-w-3xl mx-auto leading-relaxed">
            ¿Tienes alguna pregunta o quieres hacer un pedido personalizado? 
            Estamos aquí para ayudarte. ¡Nos encanta escuchar de ti!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-brown mb-6 flex items-center">
              <Send className="mr-3 text-primary" size={24} />
              Envíanos un mensaje
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-brown mb-2">
                    Nombre completo *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    className="w-full"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brown mb-2">
                    Correo electrónico *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-brown mb-2">
                  Asunto *
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="¿En qué podemos ayudarte?"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-brown mb-2">
                  Mensaje *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntanos más detalles sobre tu consulta o pedido..."
                  className="w-full min-h-32"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-dark text-brown font-medium py-3"
              >
                {isSubmitting ? "Enviando..." : "Enviar mensaje"}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Methods */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-brown mb-6">
                Otras formas de contacto
              </h2>
              
              <div className="space-y-6">
                <a 
                  href="https://wa.me/50761568575"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 p-3 rounded-lg border hover:bg-green-50 hover:border-green-200 transition-colors cursor-pointer"
                >
                  <MessageCircle className="text-green-500 mt-1" size={24} />
                  <div>
                    <h3 className="font-medium text-brown">WhatsApp</h3>
                    <p className="text-brown/70">+507 61568575</p>
                    <p className="text-sm text-brown/60">Disponible de 8:00 AM a 8:00 PM</p>
                  </div>
                </a>

                <div className="flex items-start space-x-4">
                  <MapPin className="text-red-500 mt-1" size={24} />
                  <div>
                    <h3 className="font-medium text-brown">Ubicación</h3>
                    <p className="text-brown/70">Ciudad de Panamá, Panamá</p>
                    <p className="text-sm text-brown/60">Entrega a domicilio disponible</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-brown mb-6">
                Síguenos en redes sociales
              </h2>
              
              <div className="space-y-4">
                <a 
                  href="https://www.instagram.com/viviconamoryarte/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-3 rounded-lg border hover:bg-primary/5 transition-colors"
                >
                  <Instagram className="text-pink-500" size={24} />
                  <div>
                    <p className="font-medium text-brown">Instagram</p>
                    <p className="text-sm text-brown/60">@viviconamoryarte</p>
                  </div>
                </a>

                <a 
                  href="https://www.tiktok.com/@viviconamoryarte7" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-3 rounded-lg border hover:bg-primary/5 transition-colors"
                >
                  <svg className="text-black" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.75 2h2.25a5.75 5.75 0 0 0 5.75 5.75v2.25a8 8 0 1 1-8-8v2.25a5.75 5.75 0 1 0 5.75 5.75h-2.25A3.5 3.5 0 1 1 9.25 5.75V2z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-brown">TikTok</p>
                    <p className="text-sm text-brown/60">@viviconamoryarte7</p>
                  </div>
                </a>

                <a 
                  href="https://x.com/Viviana00811243" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 p-3 rounded-lg border hover:bg-primary/5 transition-colors"
                >
                  <svg className="text-black" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-brown">X (Twitter)</p>
                    <p className="text-sm text-brown/60">@Viviana00811243</p>
                  </div>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;